import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  let title = "Something went wrong";
  let message = "An unexpected error occurred. Please try again.";
  let statusCode: number | null = null;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    switch (error.status) {
      case 404:
        title = "Page not found";
        message = "The page you're looking for doesn't exist or has been moved.";
        break;
      case 401:
        title = "Unauthorized";
        message = "You don't have permission to access this page.";
        break;
      case 403:
        title = "Access denied";
        message = "You don't have the required permissions for this resource.";
        break;
      case 500:
        title = "Server error";
        message = "Something went wrong on our end. Please try again later.";
        break;
      default:
        title = `Error ${error.status}`;
        message = error.statusText || "An unexpected error occurred.";
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
      {statusCode && (
        <span className="text-6xl font-bold text-[#2d89fe] mb-4">
          {statusCode}
        </span>
      )}
      <h1 className="text-2xl font-semibold text-[#202124] mb-2">{title}</h1>
      <p className="text-sm text-[#5f6368] mb-8 max-w-md">{message}</p>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="cursor-pointer"
        >
          Go Back
        </Button>
        <Button
          onClick={() => navigate("/dashboard", { replace: true })}
          className="cursor-pointer"
        >
          Go to Dashboard
        </Button>
      </div>

      {import.meta.env.DEV && error instanceof Error && error.stack && (
        <details className="mt-8 w-full max-w-2xl text-left">
          <summary className="text-xs text-[#5f6368] cursor-pointer hover:text-[#202124]">
            Stack trace (dev only)
          </summary>
          <pre className="mt-2 p-4 bg-[#f1f3f4] rounded-lg text-xs text-[#202124] overflow-x-auto whitespace-pre-wrap">
            {error.stack}
          </pre>
        </details>
      )}
    </div>
  );
}

export default RouteErrorBoundary;
